import { Box, Button, CircularProgress, Fade, Modal, Stack, Typography } from '@mui/material';
import { listSilobagService } from '@old/services/domain/base/silobag/base/silobag-list';
import { PropsWithChildren } from 'react';
import WarningIcon from '@old/assets/svg/warning.svg';
import i18n from '../../../libs/language';
import { useNavigate } from 'react-router';
import { PATH } from '@old/data/core/paths';
import { configButtonStylesContained } from '@old/styles/generic-styles';

type SummarySilobagList = Awaited<ReturnType<typeof listSilobagService>>['summary']['data'];

type ModalProps = {
  open: boolean;
  onClose: () => void;
};

type SilobagInsuranceProps = {
  modalInsurance: boolean;
  closeModal: () => void;
  silobagErrorList: {
    id: string;
    code: number;
  }[];
  silobagList: SummarySilobagList;
  isLoading: boolean;
};

// TODO: mover este modal al flujo posterior a seleccionar `Ver Detalle` o directamente deprecar y remover.
export function SilobagInsuranceValidation({
  modalInsurance,
  closeModal,
  silobagErrorList,
  silobagList,
  isLoading,
}: SilobagInsuranceProps) {
  const navigate = useNavigate();

  const filteredSilobagIds = silobagList
    .filter((sb) => !silobagErrorList.some((error) => error.id === sb.id))
    .map((sb) => sb.id);

  const SILOBAG_ERROR_CODES: Record<number, string> = {
    20012: i18n.t('app.silobag_insurance.error_messages.missing_initial_humidity'),
    20013: i18n.t('app.silobag_insurance.error_messages.humidity_higher_than_limit'),
    20037: i18n.t('app.silobag_insurance.error_messages.specie_not_quote'),
    20040: i18n.t('app.silobag_insurance.error_messages.has_field_closure'),
  };

  return (
    <MyModal open={modalInsurance} onClose={closeModal}>
      {isLoading ? (
        <Box width={'100%'} display={'flex'} justifyContent={'center'}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box width={'100%'}>
            <Typography variant="h4">{i18n.t('app.lab.warning_modal.attention')}</Typography>
          </Box>
          <Box sx={{ overflowY: 'scroll' }} height={'100%'}>
            <Stack spacing={2}>
              {silobagErrorList.map((sb, index) => {
                return (
                  <Box key={index}>
                    <Stack
                      display="flex"
                      justifyContent={{ xs: 'center', md: 'start' }}
                      alignItems={{ xs: 'center', md: 'start' }}
                      direction={{ xs: 'column', md: 'row' }}
                      gap={1}
                    >
                      <img src={WarningIcon} alt="warning-icon" />
                      <Typography variant="body1">
                        {i18n.t('app.silobag_insurance.error_messages.silobag_not_included')}: {sb.id}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" fontWeight={'bold'}>
                      {SILOBAG_ERROR_CODES[sb.code]}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Box>
          <Stack
            width={'100%'}
            display={'flex'}
            justifyContent={'end'}
            direction={{ xs: 'column', md: 'row' }}
          >
            <Button onClick={closeModal}>{i18n.t('app.close')}</Button>
            {silobagList.length - silobagErrorList.length > 0 && (
              <Button
                variant="contained"
                sx={configButtonStylesContained}
                onClick={() => navigate(`${PATH.SILOBAG}/insurance/${filteredSilobagIds.join(',')}`)}
              >
                {i18n.t('app.continue')}
              </Button>
            )}
          </Stack>
        </>
      )}
    </MyModal>
  );
}

function MyModal({ open, onClose, children }: PropsWithChildren<ModalProps>) {
  if (!open) return null;
  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '50%',
    overflowX: 'hidden',
    maxHeight: '95vh',
    overflowY: 'auto',
    bgcolor: 'background.paper',
    borderRadius: 4,
    boxShadow: 24,
    p: 4,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  };

  const handleClose = (_: never, reason: string) => {
    if (reason === 'backdropClick') return;
    onClose();
  };
  return (
    <Modal open={open} keepMounted onClose={handleClose}>
      <Fade in={open} appear={false} timeout={175}>
        <Box sx={style}>{children}</Box>
      </Fade>
    </Modal>
  );
}
